import { Device } from "@iunctim/utility-frontend/Resources/Public/Scripts/utilities/Device.js";

/**
 * Helper to define the overflow-states
 */
export enum State {
    Locked = "hidden",
    Unlocked = "",
}

/**
 * Class to implement the scroll-lock functionality
 * as a singleton, which can be instantiated by
 * any other extension as a dependency
 */
export class ScrollLock {
    private static instance: ScrollLock;
    private count: number = 0;
    private offset: number = 0;

    /**
     * The private constructor
     * prevents direct instantiation
     */
    private constructor() {
        // Evaluate and set the scrollbar-compensation
        const compensation = window.innerWidth - document.body.offsetWidth;
        document.body.style.setProperty(
            "--scrollbar-compensation",
            `${compensation}px`,
        );
    }

    /**
     * Method to instantiate all
     * locks on the current page
     *
     * @param {boolean} [reset=false]
     * @returns {ScrollLock}
     */
    public static use(reset: boolean = false): ScrollLock {
        if (!ScrollLock.instance) {
            ScrollLock.instance = new ScrollLock();
        }

        // Reset if requested; only used for tests
        if (reset) ScrollLock.instance.count = 0;

        return ScrollLock.instance;
    }

    /**
     * Handles the lock action
     *
     * @returns {void}
     */
    public lock(): void {
        this.count++;

        if (this.count > 0) {
            this.applyLock();
        }
    }

    /**
     * Handles the unlock action
     *
     * @returns {void}
     */
    public unlock(): void {
        this.count--;

        if (this.count === 0) {
            this.applyUnlock();
        }
    }

    /**
     * Applies the lock action
     *
     * @returns {void}
     */
    private applyLock(): void {
        document.body.style.overflow = State.Locked;
        document.body.style.paddingRight = "var(--scrollbar-compensation)";

        if (Device.iOS) {
            this.offset = window.pageYOffset;
            document.body.style.position = "fixed";
            document.body.style.top = `-${this.offset}px`;
            document.body.style.width = "100%";
        }
    }

    /**
     * Applies the unlock action
     *
     * @returns {void}
     */
    private applyUnlock(): void {
        document.body.style.overflow = State.Unlocked;
        document.body.style.paddingRight = "";

        if (Device.iOS) {
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.width = "";
            window.scrollTo(0, this.offset);
        }
    }
}
